import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { Chart } from 'chart.js';
import DistributionChartTooltip from '../DistributionChartTooltip';
import { datasetColors } from '../../../enum';

function DistributionChart(props) {
  const { question } = props;
  const chartRef = useRef();
  const canvasRef = useRef(null);
  const [tooltipModel, setTooltipModel] = useState();

  useEffect(() => {
    if (!canvasRef.current) return;

    chartRef.current = new Chart(canvasRef.current, {
      type: 'line',
      data: {
        labels: question.data.datasets[0].labels,
        datasets: question.data.datasets.map((dataset, index) => ({
          label: dataset.title,
          data: dataset.counts,
          backgroundColor: datasetColors[index % datasetColors.length],
          borderColor: datasetColors[index % datasetColors.length],
          tension: 0.4,
        })),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 0 },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            external: (context) => setTooltipModel({ ...context.tooltip }),
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { padding: 10, font: { size: 14 } },
          },
          y: {
            grid: { drawTicks: false },
            border: { display: false },
            ticks: {
              padding: 14,
              maxTicksLimit: 5,
              font: { size: 14 },
            },
          },
        },
        elements: {
          line: {
            fill: false,
            borderWidth: 2.5,
          },
          point: {
            borderWidth: 0,
            backgroundColor: 'transparent',
            hoverBorderWidth: 0,
            hoverBackgroundColor: '#222222',
            hoverRadius: 4,
          },
        },
        interaction: {
          mode: 'index',
          intersect: false,
        },
      },
      plugins: [
        {
          id: 'verticalLine',
          beforeDraw: (chart) => {
            if (!chart.tooltip?.getActiveElements().length) return;
            const elementX = chart.tooltip.getActiveElements()[0].element.x;
            chart.ctx.save();
            chart.ctx.beginPath();
            chart.ctx.moveTo(elementX, chart.scales.y.top);
            chart.ctx.lineTo(elementX, chart.scales.y.bottom);
            chart.ctx.lineWidth = 1; // eslint-disable-line no-param-reassign
            chart.ctx.strokeStyle = '#ececec'; // eslint-disable-line no-param-reassign
            chart.ctx.stroke();
            chart.ctx.restore();
          },
        },
      ],
    });
  }, []);

  return (
    <div>
      <div style={{ height: '260px' }}>
        <canvas ref={canvasRef} />
        {createPortal(
          <DistributionChartTooltip tooltipModel={tooltipModel} canvasRef={canvasRef} />,
          document.body
        )}
      </div>
    </div>
  );
}

DistributionChart.propTypes = {
  question: PropTypes.shape({
    title: PropTypes.string.isRequired,
    data: PropTypes.shape({
      averages: PropTypes.shape({
        labels: PropTypes.arrayOf(PropTypes.string).isRequired,
        values: PropTypes.arrayOf(PropTypes.number).isRequired,
        percentages: PropTypes.arrayOf(PropTypes.number).isRequired,
      }).isRequired,
      datasets: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string.isRequired,
          labels: PropTypes.arrayOf(PropTypes.string).isRequired,
          counts: PropTypes.arrayOf(PropTypes.number).isRequired,
          percentages: PropTypes.arrayOf(PropTypes.number).isRequired,
          statistics: PropTypes.shape({
            min: PropTypes.number.isRequired,
            max: PropTypes.number.isRequired,
            median: PropTypes.number.isRequired,
            mean: PropTypes.number.isRequired,
            stdDev: PropTypes.number,
            meanDiff: PropTypes.number,
            comparisonValueLabel: PropTypes.string.isRequired,
            comparisonValue: PropTypes.number,
            mode: PropTypes.number.isRequired,
          }).isRequired,
        }).isRequired
      ).isRequired,
    }).isRequired,
  }).isRequired,
};

export default DistributionChart;
